// Passkeys: the browser's WebAuthn API on the web, Apple's AuthenticationServices in the apps
// (the app's page runs on its own scheme, so WebAuthn there has to go through the native shell).
// Either way 1Password or iCloud Keychain can create and supply the passkey.
import { api, type Session } from './api';
import { isNative } from './config';
import { onNativeCommand, postNative } from './native';

type Json = Record<string, any>;

const b64uToBuf = (s: string): ArrayBuffer => {
  const pad = s.length % 4 ? '='.repeat(4 - (s.length % 4)) : '';
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
};
const bufToB64u = (b: ArrayBuffer | null | undefined): string | undefined => {
  if (!b) return undefined;
  let s = '';
  for (const x of new Uint8Array(b)) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export function passkeysSupported(): boolean {
  if (isNative) return true;
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && window.isSecureContext;
}

/** The browser can list passkeys in the email field's autofill menu. */
export async function passkeyAutofillSupported(): Promise<boolean> {
  if (isNative || !passkeysSupported()) return false;
  try {
    const PKC = window.PublicKeyCredential as unknown as { isConditionalMediationAvailable?: () => Promise<boolean> };
    return !!(await PKC.isConditionalMediationAvailable?.());
  } catch { return false; }
}

/** Asks the native shell to run a passkey ceremony; answered with a passkeyResult command. */
function nativeCeremony(kind: 'create' | 'get', options: Json): Promise<Json> {
  const requestId = Math.random().toString(36).slice(2);
  return new Promise((resolve, reject) => {
    const off = onNativeCommand((cmd) => {
      if (cmd.type !== 'passkeyResult' || cmd.requestId !== requestId) return;
      off();
      if (cmd.credential) resolve(cmd.credential as Json); else reject(new Error(cmd.error ?? 'Passkey request was cancelled'));
    });
    if (!postNative({ type: kind === 'create' ? 'passkeyCreate' : 'passkeyGet', requestId, options })) { off(); reject(new Error('Passkeys need the app')); }
  });
}

class Cancelled extends Error { name = 'AbortError'; }
/** True when the person closed the passkey sheet (not worth an error message). */
export const wasCancelled = (e: unknown) => {
  const n = (e as Error)?.name;
  return n === 'AbortError' || n === 'NotAllowedError' || /cancel/i.test((e as Error)?.message ?? '');
};

async function browserCreate(options: Json): Promise<Json> {
  const cred = (await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: b64uToBuf(options.challenge),
      user: { ...options.user, id: b64uToBuf(options.user.id) },
      excludeCredentials: (options.excludeCredentials ?? []).map((c: Json) => ({ ...c, id: b64uToBuf(c.id) })),
    } as PublicKeyCredentialCreationOptions,
  })) as PublicKeyCredential | null;
  if (!cred) throw new Cancelled('Passkey creation was cancelled');
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id, rawId: bufToB64u(cred.rawId), type: cred.type,
    response: { clientDataJSON: bufToB64u(r.clientDataJSON), attestationObject: bufToB64u(r.attestationObject), transports: r.getTransports?.() ?? [] },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

async function browserGet(options: Json, conditional: boolean, signal?: AbortSignal): Promise<Json> {
  const cred = (await navigator.credentials.get({
    ...(conditional ? { mediation: 'conditional' as CredentialMediationRequirement } : {}),
    ...(signal ? { signal } : {}),
    publicKey: { ...options, challenge: b64uToBuf(options.challenge), allowCredentials: (options.allowCredentials ?? []).map((c: Json) => ({ ...c, id: b64uToBuf(c.id) })) } as PublicKeyCredentialRequestOptions,
  })) as PublicKeyCredential | null;
  if (!cred) throw new Cancelled('Passkey sign-in was cancelled');
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id, rawId: bufToB64u(cred.rawId), type: cred.type,
    response: { clientDataJSON: bufToB64u(r.clientDataJSON), authenticatorData: bufToB64u(r.authenticatorData), signature: bufToB64u(r.signature), userHandle: bufToB64u(r.userHandle) },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

/** Creates a passkey for the signed-in person and saves it on the server. */
export async function registerPasskey(name: string): Promise<void> {
  const { challengeId, options } = await api.passkeyRegisterOptions();
  const response = isNative ? await nativeCeremony('create', options) : await browserCreate(options);
  await api.passkeyRegisterVerify(challengeId, response, name);
}

/**
 * Signs in with a passkey. `conditional` waits quietly for a pick from the email field's autofill
 * menu instead of opening a sheet (browser only); stop it with `signal`.
 */
export async function passkeyLogin(client: string | undefined, opts: { conditional?: boolean; signal?: AbortSignal } = {}): Promise<Session> {
  const { challengeId, options } = await api.passkeyLoginOptions();
  const response = isNative ? await nativeCeremony('get', options) : await browserGet(options, !!opts.conditional, opts.signal);
  return api.passkeyLoginVerify(challengeId, response, client);
}

/** A friendly default name for a new passkey. */
export function defaultPasskeyName(): string {
  const ua = navigator.userAgent;
  const where = isNative ? (/Mac/.test(ua) && !/iPhone|iPad/.test(ua) ? 'Mac app' : 'iPhone/iPad app')
    : /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Mac/.test(ua) ? 'Mac' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : 'Browser';
  return `${where} passkey`;
}
